import { BulkDocumentRequest } from "documents/_api/commands/documents-form-api-request";
import { DocumentFileType } from "documents/document-file-type";

// Mirrors DocumentUploadPolicy.MinimumFileBytes. A floor catches an empty or truncated upload and
// nothing else: a valid PDF starts around 300 bytes, so a 1 KB floor refused real one-page scans.
export const MIN_DOCUMENT_FILE_BYTES = 1;
export const MAX_DOCUMENT_FILE_BYTES = 16 * 1024 * 1024;
export const MAX_DOCUMENT_BATCH_BYTES = 64 * 1024 * 1024;

export const documentUploadFailureCodes = [
  "document_batch_too_large",
  "unsupported_file_type",
  "file_too_small",
  "file_too_large",
  "file_missing",
  "invalid_topic",
  "invalid_name",
  "document_upload_forbidden",
  "upload_failed",
  "file_type_mismatch",
  // A topic another center owns, refused by that center (#6052): a work agreement's documents.
  "not_on_the_work",
  "work_documents_closed",
  "work_agreement_not_found",
  // The same content already carries the topic (#6098): a work agreement's documents.
  "duplicate_document",
] as const;

export type DocumentUploadFailureCode = (typeof documentUploadFailureCodes)[number];

export type DocumentUploadValidationFailure = {
  request: BulkDocumentRequest;
  code: DocumentUploadFailureCode;
  issue: string;
};

export type DocumentUploadValidationResult = {
  acceptedRequests: BulkDocumentRequest[];
  failures: DocumentUploadValidationFailure[];
};

const supportedDocumentTypes = new Set<string>([
  DocumentFileType.PDF,
  DocumentFileType.DOC,
  DocumentFileType.DOCX,
  DocumentFileType.ODT,
]);
const invalidDocumentNameCharacter = /[/\\:⁄∕⧵／＼\p{Cc}\p{Cf}\p{Cs}\p{Co}]/u;
const MAX_DOCUMENT_NAME_CHARACTERS = 255;

const failureReasons: Record<
  Extract<DocumentUploadFailureCode,
    | "document_batch_too_large"
    | "unsupported_file_type"
    | "file_too_small"
    | "file_too_large"
    | "file_missing"
    | "invalid_topic"
    | "invalid_name">,
  string
> = {
  document_batch_too_large:
    "The selected files are too large to upload together. Choose fewer files and try again.",
  unsupported_file_type:
    "This file type is not supported. Choose a PDF, DOC, DOCX, or ODT file.",
  file_too_small: "This file is empty. Choose a file with content and try again.",
  file_too_large: "This file is too large. Choose a file no larger than 16 MB.",
  file_missing: "Choose a file and try again.",
  invalid_topic: "Select a valid document topic and try again.",
  invalid_name: "Enter a valid document name and try again.",
};

function hasValidTopic(request: BulkDocumentRequest): boolean {
  return Boolean(request.topics?.length) && request.topics!.every((topic) => {
    const entityClass = topic.entityClass?.trim();
    const context = topic.context?.trim();
    const hasAValue = Boolean(entityClass || context || topic.entityId);
    const entityIdHasClass = !topic.entityId || Boolean(entityClass);
    return hasAValue && entityIdHasClass;
  });
}

function validateRequest(request: BulkDocumentRequest): DocumentUploadValidationFailure | undefined {
  const file = request.file;
  if (!(file instanceof File)) {
    return { request, code: "file_missing", issue: failureReasons.file_missing };
  }
  const allowedTypes = request.fileTypes?.mediaTypes ?? supportedDocumentTypes;
  if (!allowedTypes.has(file.type.toLowerCase())) {
    return {
      request,
      code: "unsupported_file_type",
      issue: request.fileTypes?.unsupportedTypeIssue ?? failureReasons.unsupported_file_type,
    };
  }
  if (file.size < MIN_DOCUMENT_FILE_BYTES) {
    return { request, code: "file_too_small", issue: failureReasons.file_too_small };
  }
  if (file.size > MAX_DOCUMENT_FILE_BYTES) {
    return { request, code: "file_too_large", issue: failureReasons.file_too_large };
  }
  const name = request.name?.trim();
  if (
    !name ||
    name.length > MAX_DOCUMENT_NAME_CHARACTERS ||
    name.startsWith(".") ||
    invalidDocumentNameCharacter.test(name)
  ) {
    return { request, code: "invalid_name", issue: failureReasons.invalid_name };
  }
  if (!hasValidTopic(request)) {
    return { request, code: "invalid_topic", issue: failureReasons.invalid_topic };
  }
  return undefined;
}

export function validateDocumentUploadRequests(
  requests: BulkDocumentRequest[],
): DocumentUploadValidationResult {
  const acceptedRequests: BulkDocumentRequest[] = [];
  const failures: DocumentUploadValidationFailure[] = [];
  for (const request of requests) {
    const failure = validateRequest(request);
    if (failure) failures.push(failure);
    else acceptedRequests.push(request);
  }

  const acceptedBytes = acceptedRequests.reduce((total, request) => total + request.file.size, 0);
  if (acceptedBytes > MAX_DOCUMENT_BATCH_BYTES) {
    failures.push(...acceptedRequests.map((request) => ({
      request,
      code: "document_batch_too_large" as const,
      issue: failureReasons.document_batch_too_large,
    })));
    return { acceptedRequests: [], failures };
  }

  return { acceptedRequests, failures };
}
